import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Palette, Image as ImageIcon, Loader2, Save, ExternalLink, Link2 } from 'lucide-react';
import SeoAssistant from './SeoAssistant';
import SeoScoreBadge from './SeoScoreBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SWATCHES = ['#D4A24A', '#E5484D', '#F76B15', '#30A46C', '#0EA5E9', '#3E63DD', '#8E4EC6', '#EC4899'];

const PRESET_COVERS = [
  { id: 'team', label: 'Team', url: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1600&q=80' },
  { id: 'skyline', label: 'Skyline', url: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1600&q=80' },
  { id: 'office', label: 'Office', url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80' },
  { id: 'abstract', label: 'Abstract', url: 'https://images.unsplash.com/photo-1550684376-efcbd6e3f031?w=1600&q=80' },
  { id: 'gradient', label: 'Gradient', url: 'https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=1600&q=80' },
  { id: 'sunset', label: 'Sunset', url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1600&q=80' },
];

/**
 * MicrositeCustomizer — self-serve UI so each member can set a cover image
 * and accent color for their public micro-site.
 *
 * PUT /api/users/me/microsite  { microsite_cover_url, microsite_accent_color, microsite_tagline }
 *
 * Preview updates live as the user types. On save the values are persisted
 * to `users.microsite_*` fields and read back by /api/members/{id}/profile.
 */
export default function MicrositeCustomizer() {
  const { user, token } = useAuth();
  const [cover, setCover] = useState('');
  const [color, setColor] = useState('#D4A24A');
  const [tagline, setTagline] = useState('');
  const [wemmUrl, setWemmUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [seoVersion, setSeoVersion] = useState(0);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/members/${user.id}/profile`);
        const u = res.data.user || {};
        setCover(u.microsite_cover_url || '');
        setColor(u.microsite_accent_color || '#D4A24A');
        setTagline(u.microsite_tagline || '');
        setWemmUrl(u.wemm_profile_url || '');
      } catch {
        /* fall through — user just sees defaults */
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/users/me/microsite`,
        {
          microsite_cover_url: cover,
          microsite_accent_color: color,
          microsite_tagline: tagline,
          wemm_profile_url: wemmUrl,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Micro-site updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl bg-brand-surface border border-white/5 p-6 text-slate-400 flex items-center gap-2">
        <Loader2 className="animate-spin" size={16} /> Loading…
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-brand-surface border border-white/5 p-6 space-y-5" data-testid="microsite-customizer">
      <div className="flex items-start gap-3">
        <Palette className="text-brand-gold flex-shrink-0" size={22} />
        <div>
          <h3 className="text-lg font-semibold text-white">Personalize your micro-site</h3>
          <p className="text-xs text-slate-400">
            Your public profile at <span className="text-brand-gold">/member/{user?.id?.slice(0, 8)}…</span> — customize the hero image and accent color members see.
          </p>
        </div>
      </div>

      {/* Preview */}
      <div className="rounded-xl overflow-hidden border border-white/10 relative">
        <div
          className="h-32 w-full"
          style={{
            background: cover
              ? `linear-gradient(to top, #0A0A0B, transparent), url(${cover}) center/cover no-repeat`
              : `linear-gradient(135deg, ${color}30, #141416)`,
          }}
        />
        <div className="p-4 -mt-8 relative">
          <div className="w-14 h-14 rounded-full ring-4 ring-brand-surface flex items-center justify-center text-white font-semibold" style={{ background: color }}>
            {(user?.name || '?').charAt(0).toUpperCase()}
          </div>
          <p className="text-white font-serif text-lg mt-2">{user?.name || 'Your name'}</p>
          <p className="text-xs" style={{ color }}>
            {tagline || 'Your tagline shows here'}
          </p>
        </div>
      </div>

      {/* Cover URL */}
      <div>
        <label className="text-xs font-medium text-slate-300 mb-1 block flex items-center gap-1">
          <ImageIcon size={12} /> Cover image URL
        </label>
        <input
          type="url"
          value={cover}
          onChange={(e) => setCover(e.target.value)}
          placeholder="https://images.unsplash.com/… (paste any hosted image URL)"
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-brand-gold focus:border-transparent"
          data-testid="microsite-cover-input"
        />
        <p className="text-[10px] text-slate-500 mt-1">Recommended 1920×600. Leave blank to use the accent-color gradient.</p>
        <div className="flex gap-2 mt-2 flex-wrap items-center" data-testid="cover-presets">
          {PRESET_COVERS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setCover(p.url)}
              title={p.label}
              className={`w-16 h-9 rounded-md overflow-hidden border-2 transition ${cover === p.url ? 'border-brand-gold scale-105' : 'border-transparent hover:border-white/30'}`}
              data-testid={`cover-preset-${p.id}`}
            >
              <img src={p.url.replace('w=1600', 'w=160')} alt={p.label} className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
          <button
            type="button"
            onClick={() => setCover('')}
            className={`h-9 px-2 rounded-md text-[10px] border transition ${!cover ? 'border-brand-gold text-brand-gold' : 'border-white/10 text-slate-400 hover:text-white'}`}
            data-testid="cover-preset-none"
          >
            None
          </button>
        </div>
      </div>

      {/* Accent color */}
      <div>
        <label className="text-xs font-medium text-slate-300 mb-1 block flex items-center gap-1">
          <Palette size={12} /> Accent color
        </label>
        <div className="flex gap-1.5 mb-2 flex-wrap" data-testid="color-swatches">
          {SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              title={c}
              className={`w-7 h-7 rounded-full border-2 transition ${color.toUpperCase() === c ? 'border-white scale-110' : 'border-transparent hover:scale-105'}`}
              style={{ backgroundColor: c }}
              data-testid={`color-swatch-${c.slice(1)}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={color.length === 7 ? color : '#D4A24A'}
            onChange={(e) => setColor(e.target.value.toUpperCase())}
            className="h-10 w-14 rounded-lg border border-white/10 bg-transparent cursor-pointer"
            data-testid="microsite-color-picker"
          />
          <input
            type="text"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            placeholder="#D4A24A"
            className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm font-mono focus:ring-2 focus:ring-brand-gold focus:border-transparent"
            data-testid="microsite-color-hex"
          />
          <button
            onClick={() => setColor('#D4A24A')}
            className="px-3 py-2 text-xs text-slate-400 hover:text-white border border-white/10 rounded-lg"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Tagline */}
      <div>
        <label className="text-xs font-medium text-slate-300 mb-1 block">Tagline</label>
        <input
          type="text"
          value={tagline}
          onChange={(e) => setTagline(e.target.value.slice(0, 120))}
          placeholder="e.g. Helping founders scale from 6 to 7 figures"
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-brand-gold focus:border-transparent"
          maxLength={120}
          data-testid="microsite-tagline-input"
        />
        <p className="text-[10px] text-slate-500 mt-1">{tagline.length}/120</p>
      </div>

      {/* WeMillionairesMM profile URL */}
      <div className="pt-3 border-t border-white/5">
        <label className="text-xs font-medium text-slate-300 mb-1 block flex items-center gap-1">
          <Link2 size={12} className="text-brand-gold" /> WeMillionairesMM profile link
        </label>
        <input
          type="url"
          value={wemmUrl}
          onChange={(e) => setWemmUrl(e.target.value)}
          placeholder="https://wemillionairesmm.com/… (paste your WMM profile URL)"
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-brand-gold focus:border-transparent"
          data-testid="microsite-wemm-url-input"
        />
        <p className="text-[10px] text-slate-500 mt-1">
          Paste your WeMillionairesMM profile URL. When set, your Digital Card CTA and QR code both point to it —
          scan the QR to land on your WMM profile in one tap.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2 bg-brand-gold text-black font-semibold rounded-lg hover:brightness-110 disabled:opacity-50 flex items-center gap-2"
          data-testid="microsite-save"
        >
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
          Save
        </button>
        <a
          href={`/member/${user?.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 border border-white/10 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/5 flex items-center gap-2"
          data-testid="microsite-preview-link"
        >
          <ExternalLink size={14} /> Open my micro-site
        </a>
      </div>

      <SeoScoreBadge token={token} refreshKey={seoVersion} />

      <SeoAssistant
        title="SEO & AI Search (GEO)"
        description="Let AI (GPT) suggest a search-optimized title, meta description, keywords and an AI-search (GEO) summary so your micro-site gets found on Google AND cited by ChatGPT/Perplexity."
        suggestUrl={`${API_URL}/api/seo/suggest/profile`}
        saveUrl={`${API_URL}/api/seo/profile`}
        authHeader={{ Authorization: `Bearer ${token}` }}
        initial={user?.seo || null}
        onSaved={() => setSeoVersion((v) => v + 1)}
      />
    </div>
  );
}
